import { Box, Typography } from "@mui/material";
import React from "react";

import { FilterValue, PlaceGroup } from "../../types/productPlane";
import { CountryFlag, countryName } from "../common/CountryFlag";
import CountedFilterList from "../Filters/CountedFilterList";

// One group of places a country filter lists, under a caption
interface PlaceSection {
  label: string;
  groups: readonly PlaceGroup[];
  // Places shown before the group is cut
  limit: number;
}

// The groups, in the order shown: the countries first, then the regions,
// then the alliances and blocs
export const PLACE_SECTIONS: readonly PlaceSection[] = [
  { label: "Countries", groups: ["country"], limit: 10 },
  {
    label: "Regions",
    groups: ["continent", "macroregion", "subregion"],
    limit: 5,
  },
  {
    label: "Alliances & blocs",
    groups: [
      "military_alliance",
      "intelligence_alliance",
      "economic_bloc",
      "political_org",
      "security_org",
    ],
    limit: 5,
  },
];

export interface PlaceFilterListProps {
  // What the list filters by; names the lists and the box
  label: string;
  // Every place of the filter, the most frequent first: a country filter is
  // never cut
  values: FilterValue[];
  selected: readonly string[];
  onToggle: (value: string, selected: boolean) => void;
  // The text of the box that shortens the list, held by the owner
  narrowText?: string;
  onNarrowChange?: (text: string) => void;
}

// How a place reads: its name, or, for a code no place lists, the
// browser's name of the country
const placeLabel = (value: FilterValue): string =>
  value.name ?? countryName(value.value) ?? value.value;

// A country filter: its places in groups, the countries with their flags,
// each group a counted list cut at its own limit. The box that shortens the
// filter stands over the countries and shortens every group; a group other
// than the countries is left out while it has no place.
const PlaceFilterList: React.FC<PlaceFilterListProps> = ({
  label,
  values,
  selected,
  onToggle,
  narrowText,
  onNarrowChange,
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
    {PLACE_SECTIONS.map((section, index) => {
      const places = values.filter((value) =>
        section.groups.includes(value.group ?? "country"),
      );
      if (index > 0 && places.length === 0) {
        return null;
      }
      return (
        <Box key={section.label}>
          <Typography
            variant="caption"
            component="div"
            color="text.secondary"
            sx={{ fontWeight: 600, mt: index > 0 ? 0.5 : 0 }}
          >
            {section.label}
          </Typography>
          <CountedFilterList
            label={`${label}: ${section.label}`}
            values={places.map((place) => ({
              value: place.value,
              label: placeLabel(place),
              count: place.count,
              icon:
                (place.group ?? "country") === "country" ? (
                  <CountryFlag code={place.value} />
                ) : undefined,
            }))}
            selected={selected}
            onToggle={onToggle}
            limit={section.limit}
            {...(index === 0 && onNarrowChange
              ? { narrowText, onNarrowChange }
              : {})}
          />
        </Box>
      );
    })}
  </Box>
);

export default PlaceFilterList;
